import { Fragment } from 'preact'
import { Plus } from 'lucide-preact'
import type { BrandColorTokenViewDto, BrandTokenSetViewDto } from '@/types/knowledgeBase'
import { TextBox } from '@/components/common/TextBox'
import {
  aliasOf,
  colorGroups,
  inWords,
  openerOf,
  rolesPointingAt,
  storedColorValue,
  tokenNoun,
  type TokenEditor,
} from '@/components/console/knowledgeBase/brandGuidelinesModel'
import type { FontLoad } from '@/components/console/knowledgeBase/useBrandFonts'
import { AddButton, InlineForm, Refusal, RemoveQuestion, RolePills, RowActions } from './BrandEditControls'
import { BrandScaleSections } from './BrandScaleSections'
import { BrandTypeSection } from './BrandTypeSection'
import { EYEBROW, H2, LEDE, MONO, SECONDARY_BUTTON, SECTION, SWATCH_FRAME } from './brandGuidelinesStyles'
import { roleNote, type Pointers, type TokenEditing } from './brandEditing'

interface Props {
  /** Null when the company has no brand yet: every section still shows, so a person can start one. */
  set: BrandTokenSetViewDto | null
  fonts: Record<string, FontLoad>
  canChange: boolean
  pending: boolean
  /** The page's one entry editor, shared by every section so only one entry is open at a time. */
  edit: TokenEditing
  /** An editor is open somewhere on the page, a role's or an entry's, so no other can be opened until it closes. */
  locked: boolean
}

/**
 * The whole system: every color by theme, the type specimens, and the spacing, radius and shadow scales (ORB-904).
 * Every section always shows, and on the company's own page each entry is added, changed and removed in place: colors
 * since ORB-971, the rest since ORB-973.
 *
 * ⚑ Specimens sit on the brand's own Background in its own Text color, and those grounds are fixed rather than themed:
 * a light brand page stays light in the app's dark theme, because that is the page it will be.
 */
export function BrandTokenSections({ set, fonts, canChange, pending, edit, locked }: Props) {
  const pointers = rolesPointingAt(set?.Roles ?? null)
  return (
    <>
      <Colors set={set} pointers={pointers} canChange={canChange} pending={pending} edit={edit} locked={locked} />
      <BrandTypeSection set={set} fonts={fonts} pointers={pointers} canChange={canChange} pending={pending} edit={edit} locked={locked} />
      <BrandScaleSections set={set} pointers={pointers} canChange={canChange} pending={pending} edit={edit} locked={locked} />
    </>
  )
}

const COLOR_FIELDS = ['Name', 'Value', 'Usage']

function Colors({ set, pointers, canChange, pending, edit, locked }: Omit<Props, 'fonts'> & { pointers: Pointers }) {
  const groups = colorGroups(set?.Colors ?? [])
  const themes = set && set.Themes.length > 0 ? set.Themes : [{ Id: 'light', Name: 'Light' }]
  const defaultTheme = themes.find(theme => theme.Id === set?.DefaultThemeId) ?? themes[0]
  const editor = edit.editor?.kind === 'Color' ? edit.editor : null
  const count = groups.reduce((total, group) => total + group.tokens.length, 0)
  const columns = 2 + themes.length + (canChange ? 1 : 0)
  const cell = 'py-2 align-middle'

  // A color's group is the slot it is changed or added under: a new one starts with the group's name.
  const change = (token: BrandColorTokenViewDto, stem: string) =>
    edit.open('Color', token.Name, stem, { Name: token.Name, Value: storedColorValue(token, defaultTheme.Id), Usage: token.Usage ?? '' })
  const add = (stem: string | null) => edit.open('Color', null, stem, { Name: stem ? `${stem}-` : '', Value: '', Usage: '' })

  const form = (heading?: string) => editor && (
    <ColorForm editor={editor} heading={heading} themeName={defaultTheme.Name} pending={pending}
      note={editor.current === null ? null : roleNote(pointers.get(editor.current), tokenNoun('Color'))}
      onField={edit.setField} onCancel={edit.close} onSave={() => void edit.save(COLOR_FIELDS)} />
  )

  return (
    <section aria-labelledby="bg-colors-h" className={SECTION}>
      <div className="flex flex-col gap-1.5">
        <h2 id="bg-colors-h" className={H2}>Colors</h2>
        <p className={LEDE}>
          {count === 0
            ? canChange
              ? 'No colors yet. Populate from website fills this, or add a group here and type in the colors from your brand guide.'
              : 'No colors yet.'
            : <>
              {count} {count === 1 ? 'token' : 'tokens'}
              {themes.length > 1 ? `, each with ${inWords(themes.map(theme => `a ${theme.Name}`))} value` : ''}.
              {` Emails and landing pages use the ${defaultTheme.Name} theme.`}
              {canChange ? ' Colors you add, change or remove here stay that way when Populate from website runs.' : ''}
            </>}
        </p>
      </div>
      {count > 0 && (
        <div className="overflow-x-auto">
          <table aria-labelledby="bg-colors-h" className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th scope="col" className={`w-36 pr-4 pb-3 text-left ${EYEBROW}`}>Group</th>
                <th scope="col" className={`pr-6 pb-3 text-left ${EYEBROW}`}>Token</th>
                {themes.map(theme => <th key={theme.Id} scope="col" className={`w-48 pr-4 pb-3 text-left ${EYEBROW}`}>{theme.Name}</th>)}
                {canChange && <th scope="col" className="w-48 pb-3"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            {groups.map((group, groupIndex) => {
              const { stem } = group
              const addingHere = editor !== null && editor.current === null && editor.slot === stem
              return (
                <tbody key={stem} className={groupIndex > 0 ? 'border-t border-slate-200 dark:border-slate-700' : undefined}>
                  {group.tokens.map((token, index) => {
                    const alias = aliasOf(token)
                    const ref = { kind: 'Color' as const, name: token.Name, slot: stem }
                    const refusal = edit.refusalOf('Color', token.Name)
                    // The group's first and last rows carry its breathing room, so rows inside a group sit close together.
                    const pad = `${index === 0 ? 'pt-4' : ''} ${index === group.tokens.length - 1 && !canChange ? 'pb-4' : ''}`
                    const label = index === 0
                      ? <th scope="rowgroup" className="pt-4 pr-4 align-top text-left text-sm font-semibold text-slate-900 dark:text-white">{group.label}</th>
                      : <td aria-hidden="true" />
                    if (editor?.current === token.Name) {
                      return <tr key={token.Name}>{label}<td colSpan={columns - 1} className={pad}>{form()}</td></tr>
                    }
                    return (
                      <Fragment key={token.Name}>
                        <tr>
                          {label}
                          <td className={`${cell} ${pad} pr-6`}>
                            <div className="flex flex-col gap-0.5">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`${MONO} font-medium text-slate-900 dark:text-white`}>{token.Name}</span>
                                <RolePills name={token.Name} pointers={pointers} />
                              </div>
                              {alias && (
                                <span className={`${MONO} text-slate-500 dark:text-slate-400`}>
                                  <span className="sr-only">Points at </span><span aria-hidden="true">&#8594; </span>{alias}
                                </span>
                              )}
                              {token.Usage && <span className="text-slate-600 dark:text-slate-300">{token.Usage}</span>}
                            </div>
                          </td>
                          {themes.map(theme => {
                            const hex = token.ResolvedHexByTheme[theme.Id] ?? null
                            return (
                              <td key={theme.Id} className={`${cell} ${pad} pr-4`}>
                                {hex ? (
                                  <span className="inline-flex items-center gap-3">
                                    <span aria-hidden="true" className={`w-8 h-8 flex-none ${SWATCH_FRAME} rounded-md`} style={{ background: hex }} />
                                    <span className={`${MONO} text-slate-900 dark:text-white`}>{hex}</span>
                                  </span>
                                ) : <span className="text-slate-500 dark:text-slate-400">Not set</span>}
                              </td>
                            )
                          })}
                          {canChange && (
                            <td className={`${cell} ${pad} text-right`}>
                              {edit.isAsking('Color', token.Name)
                                ? <RemoveQuestion kind="Color" name={token.Name} pending={pending} className="justify-end"
                                  onRemove={() => void edit.remove(ref)} onKeep={edit.keep} />
                                : <RowActions kind="Color" name={token.Name} pending={pending || locked}
                                  onChange={() => change(token, stem)} onRemove={() => edit.ask(ref)} />}
                            </td>
                          )}
                        </tr>
                        {refusal && (
                          <tr>
                            <td aria-hidden="true" />
                            <td colSpan={columns - 1} className="pb-3"><Refusal message={refusal} /></td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                  {canChange && (
                    <tr>
                      <td aria-hidden="true" />
                      <td colSpan={columns - 1} className="pb-4">
                        {addingHere ? form() : (
                          <AddButton label="Add color" ariaLabel={`Add a color to ${group.label}`} kind="Color" slot={stem}
                            pending={pending || locked} onClick={() => add(stem)} />
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              )
            })}
          </table>
        </div>
      )}
      {canChange && (editor !== null && editor.current === null && editor.slot === null
        ? form('New group')
        : (
          <div>
            <button type="button" className={SECONDARY_BUTTON} disabled={pending || locked} data-opener={openerOf('add', 'Color', null)} onClick={() => add(null)}>
              <Plus className="w-4 h-4" aria-hidden="true" />Add group
            </button>
          </div>
        ))}
    </section>
  )
}

function ColorForm({ editor, heading, themeName, pending, note, onField, onCancel, onSave }: {
  editor: TokenEditor
  heading?: string
  themeName: string
  pending: boolean
  note: string | null
  onField: (key: string, value: string) => void
  onCancel: () => void
  onSave: () => void
}) {
  const { Name, Value, Usage } = editor.fields
  const was = editor.current === null ? null : editor.was.Value
  return (
    <InlineForm editor={editor} heading={heading} pending={pending} note={note} onCancel={onCancel} onSave={onSave}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextBox name="bg-color-name" label="Name" density="compact" value={Name} disabled={pending}
          placeholder="cyan-500" error={editor.errors.Name}
          helperText={editor.current === null && editor.slot === null ? 'The group comes from the name: cyan-500 goes in Cyan.' : undefined}
          onChange={value => onField('Name', value)} />
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className={`mt-6 w-9 h-9 flex-none rounded-md ${SWATCH_FRAME}`}
            style={{ background: Value.trim() || 'transparent' }} />
          <div className="min-w-0 flex-1">
            <TextBox name="bg-color-value" label={themeName} density="compact" value={Value} disabled={pending}
              placeholder="#0B1F3A" error={editor.errors.Value}
              helperText={was !== null && was !== Value ? `Was ${was}` : undefined}
              onChange={value => onField('Value', value)} />
          </div>
        </div>
        <div className="sm:col-span-2">
          <TextBox name="bg-color-usage" label="Usage" density="compact" value={Usage} disabled={pending}
            placeholder="What it is for, such as Accents and charts." error={editor.errors.Usage}
            onChange={value => onField('Usage', value)} />
        </div>
      </div>
    </InlineForm>
  )
}
